import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { v4 as uuid } from "uuid";
import type {
  Variable,
  Operation,
  LogicalOperator,
  SubConditionData,
  ComparativeValueType,
} from "./types";
import type { NodeVariable } from "../types";
import type {
  ApiNode,
  Conditions,
  Connections,
  Input,
} from "@/workflow_api/types/workflow";
import { serializeIfNode } from "./serialization";
import {
  deserializeIfNode,
  extractConditionFromNodeVariables,
  getOperationByTypeAndName,
} from "./serialization/deserialization";
import type { VariableType } from "../calculated-field/CalculatedFieldOperation/types";
import { parseType } from "@/workflow_api/hooks/variables/useGetVariables";

const LABEL_VALIDATION_PATTERN = /^[a-zA-Z0-9\s_\-áéíóúñÁÉÍÓÚÑ]+$/u;

const ConditionContext = createContext<
  | {
      selectedVariable: Variable | null;
      selectedOperation: Operation | null;
      comparativeValue: string | Variable | string[] | null;
      logicalOperator: LogicalOperator;
      additionalConditions: SubConditionData[];
      handleVariableChange: (variable: Variable | null) => void;
      handleOperationChange: (operation: Operation | null) => void;
      setComparativeValue: (value: string | Variable | string[]) => void;
      setLogicalOperator: (operator: LogicalOperator) => void;
      addCondition: () => void;
      updateCondition: (id: string, updates: Partial<SubConditionData>) => void;
      deleteCondition: (id: string) => void;
      addMultipleSubconditions: (conditions: SubConditionData[]) => void;
      isAddConditionDisabled: boolean;
      comparativeValueType: ComparativeValueType;
      setComparativeValueType: (type: ComparativeValueType) => void;
      getSerializedNode: (nodeVariables?: NodeVariable[]) => Input;
      setSerializedNode: (variables: Variable[], node: ApiNode) => void;
      trueLabel: string;
      setTrueLabel: (label: string) => void;
      falseLabel: string;
      setFalseLabel: (label: string) => void;
      areLabelsValid: boolean;
      trueLabelInvalid: boolean;
      falseLabelInvalid: boolean;
    }
  | undefined
>(undefined);

export const ConditionProvider = ({
  children,
  inputs,
  connections,
}: {
  children: React.ReactNode;
  inputs: Input;
  connections?: Connections;
}) => {
  const [selectedVariable, setSelectedVariable] = useState<Variable | null>(
    null,
  );

  const [trueLabel, setTrueLabel] = useState("");
  const [falseLabel, setFalseLabel] = useState("");

  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(
    null,
  );

  const [comparativeValue, setComparativeValue] = useState<
    string | Variable | string[] | null
  >(null);

  const [comparativeValueType, setComparativeValueType] =
    useState<ComparativeValueType>("input");
  const [logicalOperator, setLogicalOperator] =
    useState<LogicalOperator>("and");
  const [additionalConditions, setAdditionalConditions] = useState<
    SubConditionData[]
  >([]);

  // Initialize state from inputs on mount
  useEffect(() => {
    if (!inputs || inputs.length === 0) return;

    const conditionalInput = inputs[0];
    if (
      !conditionalInput?.conditions ||
      conditionalInput.conditions.length === 0
    )
      return;

    const firstCondition = conditionalInput.conditions[0];

    // Extract variable from first condition
    const firstInput = firstCondition.input || firstCondition.inputs?.[0];
    if (firstInput?.source_connections?.[0]?.field) {
      const field = firstInput.source_connections[0].field;
      const parsedField = parseType(field.type);
      const variable: Variable = {
        id: field.id,
        name: field.name,
        type: parsedField as VariableType,
        node_id: firstInput.source_connections[0].source_node_id,
        ref_name: "",
      };
      setSelectedVariable(variable);

      // Extract operation
      const operation = getOperationByTypeAndName(
        parsedField as VariableType,
        firstCondition.type,
      );
      setSelectedOperation(operation);

      // Extract comparative value from optional_input
      const secondInput =
        firstCondition.optional_input || firstCondition.inputs?.[1];
      if (secondInput) {
        if (secondInput.source_connections?.[0]?.field) {
          // Variable as comparative value
          const compField = secondInput.source_connections[0].field;
          const parsedCompField = parseType(compField.type);
          const compVariable: Variable = {
            id: compField.id,
            name: compField.name,
            type: parsedCompField as VariableType,
            node_id: secondInput.source_connections[0].source_node_id,
            ref_name: "",
          };
          setComparativeValue(compVariable);
          setComparativeValueType("variable");
        } else if (
          secondInput.value !== null &&
          secondInput.value !== undefined
        ) {
          // Direct value as comparative value
          setComparativeValue(secondInput.value as string);
          setComparativeValueType("input");
        }
      }
    }

    // Extract additional conditions
    if (conditionalInput.conditions.length > 1) {
      const additionalConds: SubConditionData[] = [];
      for (let i = 1; i < conditionalInput.conditions.length; i++) {
        const cond = conditionalInput.conditions[i];
        const condInput = cond.input || cond.inputs?.[0];

        let condVariable: Variable | null = null;
        if (condInput?.source_connections?.[0]?.field) {
          const field = condInput.source_connections[0].field;
          const parsedCompField = parseType(field.type);
          condVariable = {
            id: field.id,
            name: field.name,
            type: parsedCompField as VariableType,
            node_id: condInput.source_connections[0].source_node_id,
            ref_name: "",
          };
        }

        const condOperation = condVariable
          ? getOperationByTypeAndName(condVariable.type, cond.type)
          : null;

        const condSecondInput = cond.optional_input || cond.inputs?.[1];
        let condComparativeValue: string | Variable | null = null;
        let condComparativeValueType: ComparativeValueType = "input";

        if (condSecondInput) {
          if (condSecondInput.source_connections?.[0]?.field) {
            const compField = condSecondInput.source_connections[0].field;
            const parsedCompField = parseType(compField.type);
            condComparativeValue = {
              id: compField.id,
              name: compField.name,
              type: parsedCompField as VariableType,
              node_id: condSecondInput.source_connections[0].source_node_id,
              ref_name: "",
            };
            condComparativeValueType = "variable";
          } else if (
            condSecondInput.value !== null &&
            condSecondInput.value !== undefined
          ) {
            condComparativeValue = condSecondInput.value as string;
            condComparativeValueType = "input";
          }
        }

        additionalConds.push({
          id: uuid(),
          name: cond.name,
          variable: condVariable,
          operation: condOperation,
          comparativeValue: condComparativeValue ?? "",
          comparativeValueType: condComparativeValueType,
        });
      }
      setAdditionalConditions(additionalConds);

      // Set logical operator from first condition
      const operator = conditionalInput.conditions[0].operator?.toLowerCase();
      if (operator === "or" || operator === "and") {
        setLogicalOperator(operator as LogicalOperator);
      }
    }
  }, [inputs]);

  //initialize labels from connections
  useEffect(() => {
    if (!connections) return;
    if (connections["true"] && connections["true"].display_name != "True") {
      setTrueLabel(connections["true"].display_name ?? "");
    }
    if (connections["false"] && connections["false"].display_name != "False") {
      setFalseLabel(connections["false"].display_name ?? "");
    }
  }, [connections]);

  const handleVariableChange = useCallback((variable: Variable | null) => {
    setSelectedVariable(variable);
    setComparativeValue("");
    setSelectedOperation(null);
  }, []);

  const handleOperationChange = useCallback((operation: Operation | null) => {
    setComparativeValue("");
    setSelectedOperation(operation);
  }, []);

  const addCondition = useCallback(() => {
    if (
      !selectedVariable ||
      !selectedOperation ||
      (selectedOperation.needsComparativeValue && comparativeValue === "")
    ) {
      return;
    }

    setAdditionalConditions((prev) => [
      ...prev,
      {
        id: uuid(),
        variable: null,
        operation: null,
        comparativeValue: "",
        comparativeValueType: "input",
      },
    ]);
  }, [selectedVariable, selectedOperation, comparativeValue]);

  const updateCondition = useCallback(
    (id: string, updates: Partial<SubConditionData>) => {
      setAdditionalConditions((prev) =>
        prev.map((condition) =>
          condition.id === id ? { ...condition, ...updates } : condition,
        ),
      );
    },
    [],
  );

  const deleteCondition = useCallback((id: string) => {
    setAdditionalConditions((prev) =>
      prev.filter((condition) => condition.id !== id),
    );
  }, []);

  const addMultipleSubconditions = useCallback(
    (conditions: SubConditionData[]) => {
      setAdditionalConditions(conditions);
    },
    [],
  );

  const getSerializedNode = useCallback(
    (nodeVariables?: NodeVariable[]) => {
      return serializeIfNode(
        [
          {
            id: uuid(),
            comparativeValue: comparativeValue,
            operation: selectedOperation,
            variable: selectedVariable,
            comparativeValueType: comparativeValueType,
          },
          ...additionalConditions,
        ],
        logicalOperator,
        nodeVariables,
      );
    },
    [
      comparativeValue,
      selectedOperation,
      selectedVariable,
      comparativeValueType,
      additionalConditions,
      logicalOperator,
    ],
  );

  const setSerializedNode = useCallback(
    (variables: Variable[], node: ApiNode) => {
      const values = deserializeIfNode(variables, node);
      console.log(values);
      if (values) {
        // Set all values directly without triggering change handlers that reset other fields
        setSelectedVariable(values.selectedVariable);
        setSelectedOperation(values.selectedOperation);
        setComparativeValue(values.comparativeValue);
        setComparativeValueType(
          values.comparativeValue !== null &&
            typeof values.comparativeValue === "object" &&
            "id" in values.comparativeValue
            ? "variable"
            : "input",
        );
        setLogicalOperator(values.logicalOperator as LogicalOperator);
        if (!values.selectedVariable) {
          searchNodeVariables(variables, node.inputs ?? []);
        }
        processSubconditions(
          variables,
          values.additionalConditions,
          node.inputs?.[0]?.conditions,
        );
      }
    },
    [],
  );

  const searchNodeVariables = (variables: Variable[], inputs: Input) => {
    if (!inputs || inputs.length === 0) return;

    const conditionalInput = inputs[0];
    if (
      !conditionalInput?.conditions ||
      conditionalInput.conditions.length === 0
    )
      return;

    const firstCondition = conditionalInput.conditions[0];
    const extracted = extractConditionFromNodeVariables(
      variables,
      firstCondition,
    );

    if (extracted.variable) {
      setSelectedVariable(extracted.variable);
      setSelectedOperation(extracted.operation);
      setComparativeValue(extracted.comparativeValue);
      setComparativeValueType(extracted.comparativeValueType);
    }
  };

  const processSubconditions = (
    variables: Variable[],
    subconditions: SubConditionData[],
    conditions?: Conditions,
  ) => {
    const finalConditions: SubConditionData[] = [];
    subconditions.forEach((subcondition, index) => {
      if (subcondition.variable && subcondition.operation) {
        finalConditions.push(subcondition);
      } else if (conditions) {
        if (conditions.length < index + 1) return;
        const cond = conditions[index + 1]; // +1 to skip the first main condition
        const condExtracted = extractConditionFromNodeVariables(
          variables,
          cond,
        );
        finalConditions.push({
          id: uuid(),
          name: cond.name,
          variable: condExtracted.variable,
          operation: condExtracted.operation,
          comparativeValue: condExtracted.comparativeValue ?? "",
          comparativeValueType: condExtracted.comparativeValueType,
        });
      }
    });
    setAdditionalConditions(finalConditions);
  };

  const isComparativeValueValid = useMemo(() => {
    if (!selectedOperation?.needsComparativeValue) return true;

    if (comparativeValue === "") return false;

    // Si es un array, validar que no tenga items vacíos
    if (Array.isArray(comparativeValue)) {
      return (
        comparativeValue.length > 0 &&
        comparativeValue.every((item) => {
          if (typeof item === "string") {
            return item.trim() !== "";
          }
          return true; // Variables siempre son válidas
        })
      );
    }

    return true;
  }, [selectedOperation, comparativeValue]);

  const areAdditionalConditionsValid = additionalConditions.every(
    (condition) => {
      if (!condition.variable || !condition.operation) return false;

      if (!condition.operation.needsComparativeValue) return true;

      // Validar que el comparativeValue no esté vacío
      if (condition.comparativeValue === "") return false;

      // Si es un array, validar que no tenga items vacíos
      if (Array.isArray(condition.comparativeValue)) {
        return (
          condition.comparativeValue.length > 0 &&
          condition.comparativeValue.every((item) => {
            if (typeof item === "string") {
              return item.trim() !== "";
            }
            return true;
          })
        );
      }

      return true;
    },
  );

  const isAddConditionDisabled =
    !selectedVariable ||
    !selectedOperation ||
    !isComparativeValueValid ||
    !areAdditionalConditionsValid;

  const trueLabelInvalid =
    trueLabel.trim() !== "" && !LABEL_VALIDATION_PATTERN.test(trueLabel);
  const falseLabelInvalid =
    falseLabel.trim() !== "" && !LABEL_VALIDATION_PATTERN.test(falseLabel);

  const areLabelsValid = useMemo(() => {
    return (
      trueLabel.trim().length > 0 &&
      falseLabel.trim().length > 0 &&
      !trueLabelInvalid &&
      !falseLabelInvalid &&
      trueLabel.length <= 97 &&
      falseLabel.length <= 97 &&
      trueLabel !== falseLabel
    );
  }, [trueLabel, falseLabel, falseLabelInvalid, trueLabelInvalid]);

  return (
    <ConditionContext.Provider
      value={{
        selectedVariable,
        selectedOperation,
        comparativeValue,
        logicalOperator,
        additionalConditions,
        handleVariableChange,
        handleOperationChange,
        setComparativeValue,
        setLogicalOperator,
        addCondition,
        updateCondition,
        deleteCondition,
        addMultipleSubconditions,
        isAddConditionDisabled,
        comparativeValueType,
        setComparativeValueType,
        getSerializedNode,
        setSerializedNode,
        trueLabel,
        setTrueLabel,
        falseLabel,
        setFalseLabel,
        areLabelsValid,
        trueLabelInvalid,
        falseLabelInvalid,
      }}
    >
      {children}
    </ConditionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCondition = () => {
  const context = useContext(ConditionContext);
  if (context === undefined) {
    throw new Error("useCondition must be used within a ConditionProvider");
  }
  return context;
};
